"use client";

import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StepProps, BaseFormData } from "../components/types";
import { ValidationRules } from "../components/FormValidation";
import parse from "html-react-parser";
import { useFormContext, useWatch } from "react-hook-form";
import { FormField, FormItem, FormControl, FormLabel, FormMessage } from "@/components/ui/form";
import Link from "next/link";

export interface TrustDetailsStepConfig {
  showTrustType?: boolean;
  showTrusteeType?: boolean;
  showAbnAcn?: boolean;
  showTrusteeName?: boolean;
  showTrustDirectorsEmail?: boolean;
}

type TrustDetailsStepProps<T = BaseFormData> = StepProps<T>;

export const TrustDetailsStep = <T extends BaseFormData = BaseFormData>(props: TrustDetailsStepProps<T>) => {
  const { title, description } = props;
  const { control } = useFormContext();
  
  // Watch trustee type for conditional ABN field
  const selectedTrusteeType = useWatch({
    control,
    name: "trusteeType" as never
  }) as unknown as string | undefined;
  
  // Watch country of registration for conditional ABN/NZBN field
  const selectedCountry = useWatch({
    control,
    name: "countryOfRegistration" as never
  }) as unknown as string | undefined;
  
  // Extract stepConfig with type safety
  const stepConfig = props.stepConfig as TrustDetailsStepConfig | undefined;

  // Default configuration - show all fields if no config provided
  const config = stepConfig || {
    showTrustType: true,
    showTrusteeType: true,
    showAbnAcn: true,
    showTrusteeName: true,
    showTrustDirectorsEmail: false,
  };

  // Trust-specific options
  const trustTypes = [
    "Discretionary (Family) Trust",
    "Unit Trust",
    "Hybrid Trust",
    "Other"
  ];

  const trusteeTypes = [
    "Individual(s)",
    "Corporate Entity",
  ];

  return (
    <div className="flex flex-col gap-12">
      {/* Main Title */}
      <div className="flex flex-col gap-4">
        <h3>
          {title}
        </h3>
        {description && (
          <p className="paragraph-lg text-muted-foreground [&_strong]:text-foreground">
            {parse(description)}
          </p>
        )}
      </div>

      {/* Form Fields */}
      <div className="flex flex-col gap-6">
        {/* Trust Type */}
        {config.showTrustType && (
          <div className="flex flex-col gap-2">
            <FormField
              control={control}
              name={"trustType" as never}
              rules={ValidationRules.selection("trust type")}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trust Type</FormLabel>
                  <FormControl>
                    <Select
                      value={(field.value as string) || ""}
                      onValueChange={(value) => { field.onChange(value); }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {trustTypes.map((type: string) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Trustee Type */}
        {config.showTrusteeType && (
          <div className="flex flex-col gap-2">
            <FormField
              control={control}
              name={"trusteeType" as never}
              rules={ValidationRules.selection("trustee type")}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Who is the trustee?</FormLabel>
                  <FormControl>
                    <Select
                      value={(field.value as string) || ""}
                      onValueChange={(value) => { field.onChange(value); }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {trusteeTypes.map((type: string) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                  <p className="paragraph-sm text-muted-foreground">
                    The trustee is the person or company responsible for managing the trust and entering into agreements on its behalf.
                  </p>
                </FormItem>
              )}
            />
          </div>
        )}

        {/* ABN/NZBN - Only show when trustee is Corporate Entity */}
        {config.showAbnAcn && selectedTrusteeType === 'Corporate Entity' && (
          <div className="flex flex-col gap-2">
            <FormField
              control={control}
              name={"trustAbnAcn" as never}
              rules={ValidationRules.abnOrNzbn(
                selectedCountry === 'New Zealand' ? 'NZBN' : 'ABN', 
                selectedCountry
              )}
              render={({ field }) => {
                // Determine if it's Australia or New Zealand
                // const isAustralia = selectedCountry === 'Australia';
                const isNewZealand = selectedCountry === 'New Zealand';
                
                // Set field properties based on country
                const fieldLabel = isNewZealand ? 'Trustee NZBN' : 'Trustee ABN';
                const maxDigits = isNewZealand ? 13 : 11;
                const placeholder = isNewZealand ? 'e.g. 9429 000 000 000' : 'e.g. 12 345 678 901';
                const helpText = isNewZealand 
                  ? 'Only required if the trustee is a company. NZBN Lookup'
                  : 'Only required if the trustee is a company. ABN Lookup';
                const helpLink = isNewZealand 
                  ? 'https://www.nzbn.govt.nz/'
                  : 'https://abr.business.gov.au/';
                const linkText = isNewZealand ? 'NZBN Lookup' : 'ABN Lookup';

                return (
                  <FormItem>
                    <FormLabel>{fieldLabel}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={(field.value as string) || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Remove all non-digits
                          const digitsOnly = value.replace(/\D/g, '');
                          
                          // Limit digits based on country
                          const limitedDigits = digitsOnly.slice(0, maxDigits);
                          
                          // Format with appropriate mask while typing
                          let formattedValue = '';
                          if (limitedDigits.length > 0) {
                            if (isNewZealand) {
                              // NZBN format: 4-3-3-3
                              formattedValue += limitedDigits.slice(0, 4);
                              if (limitedDigits.length > 4) {
                                formattedValue += ' ' + limitedDigits.slice(4, 7);
                              }
                              if (limitedDigits.length > 7) {
                                formattedValue += ' ' + limitedDigits.slice(7, 10);
                              }
                              if (limitedDigits.length > 10) {
                                formattedValue += ' ' + limitedDigits.slice(10, 13);
                              }
                            } else {
                              // ABN format: 2-3-3-3
                              formattedValue += limitedDigits.slice(0, 2);
                              if (limitedDigits.length > 2) {
                                formattedValue += ' ' + limitedDigits.slice(2, 5);
                              }
                              if (limitedDigits.length > 5) {
                                formattedValue += ' ' + limitedDigits.slice(5, 8);
                              }
                              if (limitedDigits.length > 8) {
                                formattedValue += ' ' + limitedDigits.slice(8, 11);
                              }
                            }
                          }
                          
                          field.onChange(formattedValue);
                        }}
                        placeholder={placeholder}
                      />
                    </FormControl>
                    <p className="paragraph-sm text-muted-foreground">
                      {helpText.replace(linkText, '').trim()} You can find it on <Link href={helpLink} target="_blank" rel="noopener noreferrer" className="text-red-500 underline">{linkText}</Link> if you&apos;re unsure.
                    </p>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>
        )}

        {/* Trustee Name */}
        {config.showTrusteeName && (
          <div className="flex flex-col gap-2">
            <FormField
              control={control}
              name={"trusteeName" as never}
              rules={ValidationRules.required("trustee name")}
              render={({ field }) => {
                // Determine placeholder based on country
                const placeholder = selectedCountry === 'New Zealand' 
                  ? 'e.g. John Smith or ABC Ltd'
                  : 'e.g. John Smith or ABC Pty Ltd';

                return (
                  <FormItem>
                    <FormLabel>Trustee Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={(field.value as string) || ""}
                        onChange={(e) => { field.onChange(e); }}
                        placeholder={placeholder}
                      />
                    </FormControl>
                    <FormMessage />
                    <p className="paragraph-sm text-muted-foreground">
                      If your trustee is a company, enter the registered business name.
                    </p>
                  </FormItem>
                );
              }}
            />
          </div>
        )}

        {/* Directors / Shareholders Email */}
        {config.showTrustDirectorsEmail && (
          <div className="flex flex-col gap-2">
            <FormField
              control={control}
              name={"trustDirectorsEmail" as never}
              render={({ field }) => {
                const directorsEmailValue = (field.value as string) || "";
                const characterCount = directorsEmailValue.length;
                const maxLength = 2000;
                
                return (
                  <FormItem>
                    <FormLabel>Email addresses of other Directors / Shareholders <span className="font-normal text-muted-foreground">(Optional)</span></FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        value={directorsEmailValue}
                        onChange={(e) => { field.onChange(e); }}
                        placeholder=""
                        rows={6}
                        maxLength={maxLength}
                      />
                    </FormControl>
                    <div className="flex justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <FormMessage />
                      </div>
                      <p className="paragraph-sm text-muted-foreground whitespace-nowrap flex-shrink-0">
                        {characterCount} / {maxLength}
                      </p>
                    </div>
                  </FormItem>
                );
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
